/**
 * The Report an Issue panel's optional screenshot: Capture this screen (marked up in place,
 * over the page — MarkupEditor) or Upload an image, then a preview with Mark up / Replace /
 * Remove. The image is checked on the device first —
 * raster only, and not absurdly large — and is resized to the upload cap at submit
 * rather than refused.
 */

import { Camera, ImagePlus, PencilLine, Trash2, Upload } from "lucide-react";
import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { textNotes } from "@/lib/markup/marks";
import type { ScreenshotDraft } from "@/lib/markup/screenshot-draft";

/** Before re-encoding — anything bigger than this is a photo, not a screenshot. */
export const UPLOAD_MAX_BYTES = 25_000_000;
const ACCEPT = "image/png,image/jpeg,image/webp";

interface ScreenshotFieldProps {
  draft: ScreenshotDraft | null;
  busy: boolean;
  onCapture: () => void;
  onUpload: (file: File) => void;
  onMarkUp: () => void;
  onRemove: () => void;
}

export function ScreenshotField({
  draft,
  busy,
  onCapture,
  onUpload,
  onMarkUp,
  onRemove,
}: ScreenshotFieldProps) {
  const fileInput = useRef<HTMLInputElement | null>(null);
  const notes = draft ? textNotes(draft.marks) : [];
  const redacted = draft?.marks.some((m) => m.kind === "redact") ?? false;

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium">
        Screenshot <span className="font-normal text-muted-foreground">(optional)</span>
      </span>
      <input
        ref={fileInput}
        type="file"
        accept={ACCEPT}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) onUpload(file);
        }}
      />

      {draft ? (
        <div className="flex flex-col gap-2 rounded-md border border-border p-2">
          <button
            type="button"
            onClick={onMarkUp}
            className="group relative overflow-hidden rounded-sm bg-muted"
            aria-label="Open the screenshot to mark it up"
          >
            <img
              src={draft.previewUrl ?? draft.sourceUrl}
              alt="Screenshot to attach"
              className="max-h-40 w-full object-contain"
            />
          </button>
          <p className="text-xs text-muted-foreground">
            {draft.marks.length === 0
              ? "No marks yet — draw a box or an arrow, or add a note, to show what you mean."
              : `${draft.marks.length} mark${draft.marks.length === 1 ? "" : "s"}${
                  notes.length > 0 ? `, ${notes.length} note${notes.length === 1 ? "" : "s"}` : ""
                }${redacted ? " · part of the image is blacked out" : ""}.`}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" onClick={onMarkUp} disabled={busy}>
              <PencilLine className="size-4" />
              {draft.marks.length === 0 ? "Mark up" : "Edit markup"}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={onCapture} disabled={busy}>
              <Camera className="size-4" />
              Capture again
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => fileInput.current?.click()}
              disabled={busy}
            >
              <Upload className="size-4" />
              Upload instead
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={onRemove} disabled={busy}>
              <Trash2 className="size-4" />
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={onCapture} disabled={busy}>
            <Camera className="size-4" />
            {busy ? "Capturing…" : "Capture this screen"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInput.current?.click()}
            disabled={busy}
          >
            <ImagePlus className="size-4" />
            Upload an image
          </Button>
        </div>
      )}
    </div>
  );
}
